/**
 * Defines the FireCacheManager, a critical piece of the client-side architecture.
 * @see {@link https://firebase.google.com/docs/firestore/manage-data/enable-offline} - The concept of a local cache is fundamental to Firestore, but this manager elevates it to the application layer for object instances, not just raw data.
 *
 * This file implements a singleton pattern for Firestore document instances. Its primary purpose is to solve two major problems in real-time applications:
 * 1.  **State Consistency:** It ensures that for any given document path (e.g., 'chats/chat123'), only one corresponding reactive object exists in memory. This prevents different parts of the UI from displaying conflicting states for the same piece of data.
 * 2.  **Memory Management:** It uses a reference counting system (`acquire`/`release`). When a component or query needs a document, it "acquires" it, incrementing a counter. When it's no longer needed (e.g., the component unmounts), it "releases" it. When the counter reaches zero, the object is evicted from the cache, preventing memory leaks in long-running sessions.
 */

import type { Signal } from '@preact/signals-core';

import type { AbstractBaseInstance, AnyModelSpec } from './shared.js';

import { isEqual } from 'lodash-es';

/**
 * Internal structure for an entry within the FireCacheManager.
 * @property instance - The cached model instance.
 * @property refCount - The number of active references to this instance.
 */
interface CacheEntry {
  instance: AbstractBaseInstance<any>;
  refCount: number;
}

/**
 * Defines the signature for a factory function that can create a model instance from a Firestore snapshot.
 * @param snapshot - The Firestore snapshot containing the document's data and metadata.
 * @returns  A new instance of a firebolt model.
 */
export type FireFactory<Spec extends AnyModelSpec> = (
  snapshot: FireSnapshot<Spec['DataType']>,
) => AbstractBaseInstance<Spec['DataType']>;

/**
 * A simplified, framework-agnostic representation of a Firestore QueryDocumentSnapshot.
 * This allows the cache manager to be independent of the specific Firebase SDK version.
 * @property id - The document's ID.
 * @property ref - A reference object containing the document's path.
 * @property data - A function that returns the document's data.
 */
export interface FireSnapshot<Data extends Record<string, any>> {
  id: string;
  ref: { id: string; path: string };
  data(): Data;
}

export let debug = false;

/**
 * @class FireCacheManager
 * @classdesc A global, session-long cache for singleton instances of firebolt model objects.
 * This cache implements reference counting to automatically evict instances that
 * are no longer referenced by any active query, preventing memory leaks.
 */
export class FireCacheManager {
  constructor(protected cache = new Map<string, CacheEntry>()) {}

  /**
   * Retrieves an existing model instance from the cache or creates a new one.
   * It always increments the instance's reference count. If the instance already
   * exists, it is re-hydrated with the latest data from the snapshot.
   *
   * @template Spec - The ModelSpec definition for the model.
   * @template F - The type of the factory function.
   * @param snapshot - The Firestore snapshot for the document.
   * @param factory - The factory function (e.g., `createClientInstance`) used to create a new instance if one doesn't exist.
   * @returns The singleton instance for the document.
   */
  acquire<Spec extends AnyModelSpec, F extends FireFactory<Spec>, Snap extends FireSnapshot<Spec['DataType']>>(
    snapshot: Snap,
    factory: F,
  ): ReturnType<F> {
    const { id, path } = snapshot.ref;
    if (debug) console.debug(`[FirestoreCache] Acquiring ${factory.name} (cache size: ${this.cache.size})`);
    if (this.cache.has(path)) {
      if (debug)
        console.debug(
          `[FirestoreCache] Reusing ${factory.name}(${id}) for ${path} (refCount=${this.cache.get(path)?.refCount})`,
        );
      const entry = this.cache.get(path)!;
      entry.refCount++;
      // Hydrate with the latest data, even if it's an existing instance.
      const data = snapshot.data();

      // Peek at the current data without triggering reactive updates
      const currentData: Record<string, unknown> = {};
      for (const key of Object.keys(data)) {
        const value = (entry.instance as unknown as Record<string, unknown>)[key];
        // Use peek if available (for signals), otherwise use the value directly
        currentData[key] = value && typeof value === 'object' && 'peek' in value ? (value as Signal).peek() : value;
      }

      // Only hydrate if the data has actually changed
      if (isEqual(currentData, data)) {
        if (debug) console.debug(`[FirestoreCache] Skipping hydration for ${factory.name}(${id}) - data unchanged`);
      } else {
        // Note: Client code should wrap hydration calls in batch() when using signals
        if (entry.instance['_hydrate']) {
          if (debug) console.debug(`[FirestoreCache] Hydrating ${factory.name}(${id}) for ${path} with _hydrate`);
          entry.instance['_hydrate'](data);
        } else {
          if (debug)
            console.debug(
              `[FirestoreCache] Hydrating ${factory.name}(${id}) for ${path} with Object.assign (missing _hydrate)`,
            );
          Object.assign(entry.instance, data);
        }
      }
      if (debug) console.debug(`[FirestoreCache] Hydrated ${factory.name}(${id}) for ${path}`, entry.instance);
      return entry.instance as ReturnType<F>;
    } else {
      if (debug) console.debug(`[FirestoreCache] Creating new instance ${factory.name}(${id}) for ${path}`);
      const instance = factory(snapshot);
      if (debug) console.debug(`[FirestoreCache] Created new instance ${factory.name}(${id}) for ${path}`, instance);
      this.cache.set(path, { instance, refCount: 1 });
      return instance as ReturnType<F>;
    }
  }

  /**
   * Decrements the reference count for a model instance by its document path.
   * If the reference count drops to zero, the instance is evicted from the cache,
   * allowing it to be garbage collected.
   *
   * @param path - The full document path of the instance to release.
   */
  release(path: string): void {
    if (debug) console.debug(`[FirestoreCache] Releasing ${path} (cache size: ${this.cache.size})`);
    if (this.cache.has(path)) {
      if (debug) console.debug(`[FirestoreCache] Current refCount for ${path}: ${this.cache.get(path)?.refCount}`);
      const entry = this.cache.get(path)!;
      entry.refCount--;
      if (entry.refCount <= 0) {
        if (debug) console.debug(`[FirestoreCache] Evicting ${path} from cache`);
        this.cache.delete(path);
      } else {
        if (debug) console.debug(`[FirestoreCache] Decremented refCount for ${path} to ${entry.refCount}`);
      }
    }
  }
}

/**
 * The singleton instance of the FireCacheManager used throughout the application.
 */
export const FirestoreCache = new FireCacheManager();
